import { FunctionComponent, forwardRef, ReactNode, PropsWithoutRef, Ref, MouseEvent } from 'react';

import {
  AvatarProps,
  Avatar,
  Flex,
  Icon,
  registerIcon,
  LinkProps,
  SummaryItem,
  Image,
  FlexContainerProps,
  EmojiDisplay,
  useI18n,
  ForwardProps,
  BaseProps,
} from '@pega/cosmos-react-core';
import BareButton from '@pega/cosmos-react-core/lib/components/Button/BareButton';
import * as downloadIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/download.icon';
import * as documentDocIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/document-doc.icon';
import * as chainUpIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/chain-up.icon';

import {
  StyledMessageContainer,
  StyledMessageMain,
  StyledMessageBubble,
  StyledMessageBubbleContent,
  StyledMediaList,
  StyledMediaListItem,
  StyledMediaThumbNail,
  StyledSummaryItem,
  StyledMediaLink,
  StyledMediaDownloadButton,
  StyledMetaInfoContainer,
  StyledMetaInfo,
  StyledBlinkingDot,
  StyledMediaButton,
  StyledMessageHeader,
  StyledMessageHeaderContent,
  StyledMessageHeaderMeta,
} from './Message.styles';
import React from 'react';

registerIcon(downloadIcon, documentDocIcon, chainUpIcon);

export interface AttachmentItemProps extends BaseProps {
  /** Unique Id for this attachment */
  id: string;
  /** Name of the attachment. */
  name: string;
  /**
   * A Cosmos icon name identifier to use for a attachment. Will serve as a fallback to a broken thumbnail.
   * @default "document-doc"
   */
  icon?: string;
  /** A string to be used as an image src for a attachment thumbnail. Falls back to a provided icon or the default attachment icon. */
  thumbnail?: string;
  /** Additional information about the attachment. If progress prop is passed and its value is less than 100, this region is instead used for the upload progress indicator. */
  meta?: string;
  /** When passed, previews the attachment on click */
  onPreview?: (id: string) => void;
  /** When passed, this will render a single icon button or within a MenuButton if onDownload is defined. */
  onDownload?: (id: string) => void;
  /** When passed, this will render a single icon button or within a MenuButton if onDelete is defined. */
  onDelete?: (id: string) => void;
}

export interface MediaPageLinks extends Pick<LinkProps, 'href'> {
  /** Unique id for this push link */
  id: string;
  /** A string to be used as an image src for a thumbnail. */
  thumbnail?: AttachmentItemProps['thumbnail'];
  /** Additional information about the linked resource. */
  title?: string;
}

export type MessageHeaderProps = {
  /** message header content */
  content?: ReactNode;
  /** meta data */
  meta?: ReactNode;
};

export interface MessageProps {
  /** ref to the message wrapper */
  ref?: Ref<HTMLDivElement>;
  /** Message to be displayed */
  message?: ReactNode;
  /** Attachment list */
  attachments?: AttachmentItemProps[];
  /** Message page push links list */
  pagePushLinks?: MediaPageLinks[];
  /** 3 themes supported by default to differentiate between sender, receiver and other */
  variant: 'sender' | 'receiver' | 'other';
  /** timestamp of the message(Formatted) */
  timeStamp?: string;
  /** Incoming message/ outgoing message */
  direction: 'in' | 'out';
  /** Avatar information, can be image and name */
  avatarInfo?: Pick<AvatarProps, 'name' | 'imageSrc'>;
  /** Content type */
  contentType?: 'rich-text' | 'plain-text';
  /** Message status */
  status?: string;
  /** Indicates if this message is being currently typed */
  typing?: boolean;
  /** Message header */
  messageHeader?: MessageHeaderProps;
}

const Message: FunctionComponent<MessageProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MessageProps>, ref: MessageProps['ref']) => {
    const {
      message,
      direction,
      variant,
      avatarInfo,
      timeStamp,
      attachments = [],
      pagePushLinks = [],
      status,
      typing,
      messageHeader,
      ...restProps
    } = props;

    const t = useI18n();

    let messageMetaJustify: FlexContainerProps['justify'] = 'between';
    if (timeStamp && !status) {
      messageMetaJustify = 'start';
    } else if (!timeStamp && status) {
      messageMetaJustify = 'end';
    }

    return (
      <Flex
        as={StyledMessageContainer}
        ref={ref}
        container={{
          gap: 0.5,
          direction: direction === 'in' ? 'row' : 'row-reverse',
        }}
        item={{
          shrink: 0,
        }}
        messageDirection={direction}
        variant={variant}
        typing={typing}
        messageHeader={messageHeader}
        {...restProps}
      >
        {avatarInfo && <Avatar name={avatarInfo.name} size="m" shape="circle" imageSrc={avatarInfo.imageSrc} />}
        <Flex
          container={{
            direction: 'column',
            alignItems: direction === 'in' ? 'start' : 'end',
            justify: 'center',
          }}
          as={StyledMessageMain}
        >
          <StyledMessageBubble>
            {messageHeader && (
              <Flex container={{ gap: 0.5, justify: 'between' }} as={StyledMessageHeader}>
                <StyledMessageHeaderContent>{messageHeader.content}</StyledMessageHeaderContent>
                <StyledMessageHeaderMeta>{messageHeader.meta}</StyledMessageHeaderMeta>
              </Flex>
            )}
            {message && (
              <StyledMessageBubbleContent>
                <EmojiDisplay content={message} size={20} />
              </StyledMessageBubbleContent>
            )}
            {(attachments.length > 0 || pagePushLinks.length > 0) && (
              <StyledMediaList>
                {attachments.map((attachment) => {
                  const { id, icon, name, meta, onDownload, onPreview, thumbnail } = attachment;
                  return (
                    <Flex
                      key={id}
                      as={StyledMediaListItem}
                      container={{
                        direction: 'column',
                      }}
                    >
                      {thumbnail && (
                        <Image
                          as={StyledMediaThumbNail}
                          src={thumbnail}
                          onClick={(e: MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onPreview?.(id);
                          }}
                          alt={name}
                        />
                      )}
                      <SummaryItem
                        as={StyledSummaryItem}
                        primary={
                          <StyledMediaButton
                            onClick={(e: MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onPreview?.(id);
                            }}
                          >
                            {name}
                          </StyledMediaButton>
                        }
                        secondary={meta}
                        visual={<Icon name={icon || 'document-doc'} />}
                        actions={
                          <BareButton
                            aria-label={t('download')}
                            as={StyledMediaDownloadButton}
                            onClick={(e: MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onDownload?.(id);
                            }}
                          >
                            <Icon name="download" />
                          </BareButton>
                        }
                      />
                    </Flex>
                  );
                })}
                {pagePushLinks.map((pagePushLink) => {
                  const { id, href, title, thumbnail } = pagePushLink;
                  return (
                    <Flex
                      as={StyledMediaListItem}
                      key={id}
                      container={{
                        direction: 'column',
                      }}
                    >
                      {thumbnail && <Image as={StyledMediaThumbNail} src={thumbnail} alt={title || href} />}
                      <SummaryItem
                        as={StyledSummaryItem}
                        primary={
                          <StyledMediaLink aria-label={title} href={href} target="_blank" rel="noreferrer">
                            {href}
                          </StyledMediaLink>
                        }
                        visual={<Icon name="chain-up" />}
                      />
                    </Flex>
                  );
                })}
              </StyledMediaList>
            )}
          </StyledMessageBubble>
          {!typing && (
            <Flex
              as={StyledMetaInfoContainer}
              container={{
                justify: messageMetaJustify,
                gap: 1.5,
              }}
            >
              {timeStamp && <StyledMetaInfo>{timeStamp}</StyledMetaInfo>}
              {status && <StyledMetaInfo>{status}</StyledMetaInfo>}
            </Flex>
          )}
          {typing && (
            <Flex
              container={{
                gap: 0.25,
              }}
            >
              <StyledBlinkingDot delay={0} />
              <StyledBlinkingDot delay={0.25} />
              <StyledBlinkingDot delay={0.5} />
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  },
);

export default Message;
